<template>
  <b-sidebar
    id="eod-question-sidebar"
    z-index="51001"
    title="Edit question"
    right
    shadow
    :visible="active"
    backdrop
    no-header
    sidebar-class="sidebar-lg"
    header-class="header-dark"
    bg-variant="white"
    width="420px"
    @change="$emit('update:active', $event)"
    @hidden="clearForm"
  >
    <template #default="{ hide }" v-if="question">
      <!-- Header -->
      <div
        class="header-dark d-flex justify-content-between align-items-center content-sidebar-header px-4 py-3"
      >
        <vs-dropdown class="cursor-pointer flex" vs-custom-content>
          <feather-icon
            :icon="questionTypeMap[question.type] ? questionTypeMap[question.type].icon : 'HelpCircleIcon'"
            class="p-2 inline-flex rounded-lg bg-white border border-warning shadow-sm"
            svgClasses="h-5 w-5"
          />

          <vs-dropdown-menu style="z-index: 200001">
            <vs-dropdown-item
              :disabled="true"
              class="px-0 py-2"
              v-for="(typeKey, index) in Object.keys(questionTypeMap)"
              :key="index"
            >
              <feather-icon
                :icon="questionTypeMap[typeKey].icon"
                class="p-2 inline-flex rounded-lg bg-white border border-light-grey shadow-sm"
                :class="{
                  'border-warning text-warning stroke-warning': typeKey === question.type,
                }"
                svgClasses="h-5 w-5"
              />
              <span class="ml-3">{{ questionTypeMap[typeKey].name }}</span>
            </vs-dropdown-item>
          </vs-dropdown-menu>
        </vs-dropdown>

        <div>
          <feather-icon
            icon="TrashIcon"
            class="cursor-pointer mr-2"
            svgClasses="h-5 w-5 stroke-2"
            @click="$emit('delete:question', question); hide()"
          />
          <feather-icon
            class="ml-3 cursor-pointer"
            icon="XIcon"
            svgClasses="h-5 w-5 stroke-2"
            @click="hide"
          />
        </div>
      </div>

      <div class="px-4">
        <form>
          <div class="vx-row">
            <div class="vx-col w-full">
              <label class="mt-4 form-label">Question type</label>
              <p>{{ questionTypeMap[question.type] ? questionTypeMap[question.type].name : question.type }}</p>

              <label class="form-label">Title</label>
              <vs-input
                :disabled="!isEditable"
                name="title"
                v-model="question.title"
                class="w-full mb-4"
                placeholder="Title"
              />

              <label class="form-label">Help text <small>(Optional)</small></label>
              <vs-textarea
                :disabled="!isEditable"
                rows="5"
                v-model="question.helpText"
                placeholder="Help text"
              />

              <label class="form-label mt-3">More info text <small>(Optional, shown in info bubble)</small></label>
              <vs-textarea
                :disabled="!isEditable"
                rows="3"
                v-model="question.moreInfoText"
                placeholder="More info text"
              />

              <!-- Venue filter -->
              <label class="form-label">Venue filter <small>(leave empty for all venues)</small></label>
              <VenueSelector
                :deactivated="!isEditable"
                v-model="question.venueIds"
                :multiple="true"
                :filterCountries="countryName ? [countryName] : []"
              />

              <!-- Rating-specific configuration -->
              <template v-if="question.type === 'rating'">
                <vs-divider class="mt-4 mb-2" />
                <label class="form-label">Rating labels</label>
                <small class="mb-2 d-block" style="color: #999">Value 0 is the default (not answered). Edit labels to customize.</small>

                <div
                  v-for="(label, idx) in question.labels"
                  :key="idx"
                  class="d-flex align-items-center mb-2"
                  style="max-width: 100%"
                >
                  <span class="mr-2 label-chip" :class="{ 'label-chip--default': idx === 0 }">{{ idx }}</span>
                  <div style="flex: 1; min-width: 0">
                    <vs-input
                      :value="label"
                      :disabled="!isEditable"
                      @input="updateLabel(idx, $event)"
                      :placeholder="'Label for ' + idx"
                    />
                  </div>
                  <feather-icon
                    v-if="isEditable && idx === question.labels.length - 1 && question.labels.length > 2"
                    icon="MinusCircleIcon"
                    svgClasses="h-5 w-5"
                    class="ml-2 cursor-pointer text-danger"
                    style="flex-shrink: 0"
                    @click="removeLastLabel"
                  />
                </div>

                <vs-button
                  v-if="isEditable"
                  size="small"
                  color="primary"
                  type="border"
                  class="mt-2 mb-4"
                  @click="addLabel"
                >+ Add rating value</vs-button>

                <vs-divider class="mt-2 mb-2" />

                <vs-checkbox
                  :disabled="!isEditable"
                  v-model="question.requireCommentWhenLowScore"
                  class="mt-2 mb-2"
                >
                  Require comment when score is low
                </vs-checkbox>
                <template v-if="question.requireCommentWhenLowScore">
                  <label class="form-label">Comment required when score is at or below</label>
                  <vs-select
                    :disabled="!isEditable"
                    v-model="question.commentRequiredWhenScoreAtOrBelow"
                    class="w-full mb-4"
                  >
                    <vs-select-item
                      v-for="(label, idx) in question.labels.slice(1)"
                      :key="idx + 1"
                      :value="idx + 1"
                      :text="(idx + 1) + ' - ' + label"
                    />
                  </vs-select>
                </template>
              </template>

              <!-- Text-specific fields -->
              <template v-if="question.type === 'text'">
                <vs-divider class="mt-4 mb-2" />
                <vs-checkbox
                  :disabled="!isEditable"
                  v-model="question.isAnswerRequired"
                  class="mt-2 mb-2"
                >
                  Answer required
                </vs-checkbox>
                <vs-checkbox
                  :disabled="!isEditable"
                  v-model="question.createVenueTask"
                  class="mt-2 mb-2"
                >
                  Create venue task when answered
                </vs-checkbox>
              </template>

              <!-- Yes/No-specific fields -->
              <template v-if="question.type === 'yesNo'">
                <vs-divider class="mt-4 mb-2" />
                <vs-checkbox
                  :disabled="!isEditable"
                  v-model="question.requireCommentWhenYes"
                  class="mt-2 mb-2"
                >
                  Require comment when "Yes"
                </vs-checkbox>
              </template>

              <!-- Time-specific fields -->
              <template v-if="question.type === 'time'">
                <vs-divider class="mt-4 mb-2" />
                <label class="form-label">Default Value</label>
                <vs-input class="w-full" v-model="question.defaultValue" :disabled="!isEditable" placeholder="04:00" />
              </template>

              <vs-row class="flex flex-wrap items-center mt-4">
                <vs-button
                  class="mr-5 rounded-lg"
                  color="warning"
                  :disabled="!isEditable"
                  @click="$emit('update:question'); hide()"
                >Save</vs-button>
                <vs-button class="rounded-lg" color="primary" @click="hide">Cancel</vs-button>
              </vs-row>
            </div>
          </div>
        </form>
      </div>
    </template>
  </b-sidebar>
</template>

<script>
import { mapGetters } from "vuex";
import VenueSelector from "@/components/VenueSelector.vue";

export default {
  components: { VenueSelector },
  props: {
    isEditable: {
      type: Boolean,
      default: false,
    },
    question: {
      type: Object,
      default: function() { return null; },
    },
    active: {
      type: Boolean,
      default: false,
    },
    countryName: {
      type: String,
      default: null,
    },
  },
  emits: ["update:active", "update:question", "delete:question"],
  computed: {
    ...mapGetters({
      questionTypeMap: "eodTemplates/questionTypeMap",
    }),
  },
  watch: {
    question: function(val) {
      if (val && val.type === "rating" && (!val.labels || val.labels.length === 0)) {
        this.$set(val, "labels", ["Not rated", "Awful", "Not good", "Alright", "Good", "Awesome"]);
        val.minValue = 0;
        val.maxValue = 5;
      }
    },
  },
  methods: {
    updateLabel: function(idx, value) {
      if (this.question && this.question.labels) {
        this.$set(this.question.labels, idx, value);
      }
    },
    addLabel: function() {
      if (this.question && this.question.labels) {
        this.question.labels.push("Rating " + this.question.labels.length);
        this.question.maxValue = this.question.labels.length - 1;
      }
    },
    removeLastLabel: function() {
      if (this.question && this.question.labels && this.question.labels.length > 2) {
        this.question.labels.pop();
        this.question.maxValue = this.question.labels.length - 1;
      }
    },
    clearForm: function() {
      // no-op
    },
  },
};
</script>

<style scoped lang="css" src="bootstrap/dist/css/bootstrap.min.css"></style>

<style scoped>
@import "https://cdnjs.cloudflare.com/ajax/libs/bootstrap-vue/2.22.0/bootstrap-vue.min.css";

.header-dark {
  background-color: #f6f6f6;
}
.form-label {
  color: #5e5873;
  font-size: 0.857rem;
}
.label-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 26px;
  border-radius: 13px;
  background: rgba(var(--vs-primary), 1);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
  padding: 0 6px;
}
.label-chip--default {
  background: #b8c2cc;
}
</style>
